import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const CategoryNode = ({ cat, isHovered, onMouseEnter, onMouseLeave }) => {
    return (
        <div
            className="relative flex flex-col items-center group"
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            {/* HUD Popup Callout */}
            <AnimatePresence>
                {isHovered && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 z-50 pointer-events-none w-[200px] sm:w-[220px]">
                        {/* SVG Connecting Line - Hidden on very small screens to avoid clutter */}
                        <svg className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full h-[60px] sm:h-[100px] hidden sm:block" overflow="visible">
                            <motion.path
                                initial={{ pathLength: 0, opacity: 0 }}
                                animate={{ pathLength: 1, opacity: 1 }}
                                d="M 110 100 L 110 70 L 150 40"
                                fill="none"
                                stroke={cat.color}
                                strokeWidth="1.5"
                                strokeDasharray="3 3"
                            />
                            <motion.circle
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                cx="150" cy="40" r="3" fill={cat.color}
                            />
                        </svg>

                        {/* The Info Window */}
                        <motion.div
                            initial={{ opacity: 0, x: 0, y: 10, scale: 0.9 }}
                            animate={{ opacity: 1, x: 0, y: -10, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.2 } }}
                            className="relative p-3 sm:p-4 bg-white backdrop-blur-xl rounded-[16px] sm:rounded-[20px] border border-white/10 shadow-[0_15px_35px_rgba(0,0,0,0.4)]"
                        >
                            <div className="flex items-center gap-2.5 mb-4">
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-slate-900/5 border border-white/5" style={{ color: cat.color }}>
                                    <FontAwesomeIcon icon={cat.icon} size="sm" />
                                </div>
                                <div>
                                    <h5 className="text-[9px] font-black text-slate-900 uppercase tracking-widest leading-none font-display">Record Data</h5>
                                    <span className="text-[7px] text-slate-400 font-bold uppercase tracking-widest italic font-sans">{cat.label}</span>
                                </div>
                            </div>

                            <div className="space-y-2.5">
                                <div className="flex justify-between items-center bg-slate-900/5 p-2 rounded-lg border border-white/5">
                                    <span className="text-[8px] font-bold text-slate-900/30 uppercase tracking-[0.2em] font-sans">Global</span>
                                    <span className="text-[11px] font-black text-slate-900 tracking-wider font-mono">{cat.stats.world}</span>
                                </div>
                                <div className="flex justify-between items-center bg-slate-900/5 p-2 rounded-lg border border-white/5">
                                    <span className="text-[8px] font-bold text-slate-900/30 uppercase tracking-[0.2em] font-sans">Sri Lanka</span>
                                    <span className="text-[11px] font-black text-slate-900 tracking-wider font-mono">{cat.stats.sl}</span>
                                </div>
                                <div className="flex justify-between items-center bg-emerald-500/10 p-2 rounded-lg border border-emerald-500/10">
                                    <span className="text-[8px] font-black text-emerald-400 uppercase tracking-[0.2em] font-sans">Endemic</span>
                                    <span className="text-[11px] font-black text-emerald-400 tracking-wider font-mono">{cat.stats.endemic}</span>
                                </div>
                            </div>

                            {/* Micro Decorative Edge */}
                            <div className="absolute top-2 right-2 w-2 h-2 border-t border-r border-white/10 rounded-tr" />
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* The Node Button */}
            <Link to={`/flora-fauna/category/${cat.id}`}>
                <motion.button
                    whileHover={{ scale: 1.1, y: -5 }}
                    whileTap={{ scale: 0.95 }}
                    className={`
                        relative w-16 h-16 rounded-full flex flex-col items-center justify-center transition-all duration-500
                        ${isHovered ? 'bg-slate-900 border-white/20 shadow-2xl' : 'bg-white border-slate-100 shadow-premium'}
                        border-2 z-20 overflow-hidden
                    `}
                >
                    {/* Active Glow Rings */}
                    {isHovered && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1.2 }}
                            className="absolute inset-0 rounded-full blur-xl"
                            style={{ backgroundColor: `${cat.color}20` }}
                        />
                    )}

                    <FontAwesomeIcon
                        icon={cat.icon}
                        style={{ color: isHovered ? '#ffffff' : cat.color, fontSize: '24px' }}
                        className="relative z-10 transition-colors duration-500"
                    />

                    {/* Label Connector Dot */}
                    <div className={`absolute bottom-2 w-1 h-1 rounded-full transition-all duration-500 ${isHovered ? 'bg-emerald-500 scale-150' : 'bg-slate-200'}`} />
                </motion.button>
            </Link>

            {/* Two-Line Label */}
            <div className="mt-8 text-center relative">
                <h4
                    className="text-[11px] font-black uppercase tracking-[0.2em] leading-none mb-1.5 transition-colors duration-500 font-display"
                    style={{ color: isHovered ? cat.color : '#0f172a' }}
                >
                    {cat.label}
                </h4>
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest italic opacity-80 group-hover:opacity-100 transition-opacity font-sans">
                    {cat.subLabel}
                </p>

                {/* Active Underline */}
                <motion.div
                    className="absolute -bottom-4 left-1/2 -translate-x-1/2 h-0.5 rounded-full"
                    style={{ backgroundColor: cat.color }}
                    initial={{ width: 0 }}
                    animate={{ width: isHovered ? '30%' : 0 }}
                    transition={{ duration: 0.3 }}
                />
            </div>
        </div>
    );
};

export default CategoryNode;
